import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface CompanyInfo {
  id: string;
  name: string;
  domain: string | null;
  subscription_tier: string | null;
  active: boolean;
}

/**
 * Enhanced company info hook with React Query caching and optimized column selection
 * Caches company data for 5 minutes to eliminate redundant lookups
 */
export const useCompanyInfo = () => {
  const { user } = useAuth();

  const { data: companyInfo, isLoading: loading, error } = useQuery({
    queryKey: ['company-info', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Get user's active company with optimized column selection
      const { data: association, error: associationError } = await supabase
        .from('user_company_associations')
        .select('company_id, companies!inner(id, name, domain, subscription_tier, active)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (associationError) {
        console.error('Error fetching user company association:', associationError);
        throw new Error('Failed to fetch user company association');
      }

      if (!association?.companies) {
        console.log('User has no active company assigned');
        return null;
      }

      return association.companies as CompanyInfo;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  return { 
    companyInfo: companyInfo ?? null, 
    loading, 
    error: error ? 'Failed to load company info' : null 
  };
};
